import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CamWebPushConfig, CamWebPushPermission, CamWebPushServiceWorkerState } from '../../services/dto/web-push';
import { CamWebPushService } from '../../services/web-push.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cam-web-push-manager',
  template: '',
})
export class CamWebPushManagerComponent implements OnInit {
  public autoEnable = input(false);

  public errorOccurred = output<Error>();

  public permissionChange = output<CamWebPushPermission>();

  public swScope = input<string>();

  public swUrl = input<string>();

  public swStateChange = output<CamWebPushServiceWorkerState>();

  public unsubscribed = output();

  public vapidPublicKey = input<string>();

  public subscribed = output<PushSubscription>();

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _webPushService = inject(CamWebPushService);

  public async enable(): Promise<void> {
    try {
      await this._webPushService.enable(this._overrides());
    } catch (error) {
      this.errorOccurred.emit(error instanceof Error ? error : new Error(String(error)));
    }
  }

  async ngOnInit(): Promise<void> {
    this._wireOutputs();
    try {
      await this._webPushService.init(this._overrides());
      if (this.autoEnable()) {
        await this.enable();
      }
    } catch (error) {
      this.errorOccurred.emit(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private _overrides(): Partial<CamWebPushConfig> {
    const swScope = this.swScope();
    const swUrl = this.swUrl();
    const vapidPublicKey = this.vapidPublicKey();
    return {
      ...(swScope !== undefined ? { swScope } : {}),
      ...(swUrl !== undefined ? { swUrl } : {}),
      ...(vapidPublicKey !== undefined ? { vapidPublicKey } : {}),
    };
  }

  private _wireOutputs(): void {
    this._webPushService.permission$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(permission => this.permissionChange.emit(permission));

    this._webPushService.swState$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(state => this.swStateChange.emit(state));

    this._webPushService.subscription$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(subscription => {
      if (subscription) {
        this.subscribed.emit(subscription);
      } else {
        this.unsubscribed.emit();
      }
    });
  }
}
